import { memo } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import { SideBarScroll } from "src/views/assistant/components/ui/SideBarScroll";
import type { Dataset } from "src/views/assistant/stores/ChatStore";

interface ReferenceDatasetsTabProps {
  datasets: Dataset[];
}

/**
 * ReferenceDatasetsTab - Displays reference datasets used for proposal generation.
 *
 * Each dataset is a clickable item that opens the dataset viewer in a new window.
 * Follows the same pattern as InputDocumentsTab for sidebar consistency.
 */
const ReferenceDatasetsTab = memo(({ datasets }: ReferenceDatasetsTabProps) => {
  if (!datasets.length) {
    return (
      <NoContentCard
        title="No reference datasets"
        description="Reference datasets will appear here once datasets are selected for report generation."
      />
    );
  }

  const handleDatasetClick = (dataset: Dataset) => {
    window.open(
      `/datasets/${dataset.id}/viewer`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <SideBarScroll>
      <Box sx={{ px: 1, pt: 0.5 }}>
        <List disablePadding data-testid="pg-ref-datasets-list">
          {datasets.map((dataset) => (
            <ListItemButton
              key={dataset.id}
              data-testid={`pg-ref-dataset-item-${dataset.id}`}
              onClick={() => handleDatasetClick(dataset)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                "&:hover .open-icon": {
                  opacity: 1,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <StorageOutlinedIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    noWrap
                    title={dataset.name}
                    sx={{ fontWeight: 500 }}
                  >
                    {dataset.name}
                  </Typography>
                }
                secondary={
                  dataset.description ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                      title={dataset.description}
                    >
                      {dataset.description}
                    </Typography>
                  ) : null
                }
              />
              <OpenInNewIcon
                className="open-icon"
                sx={{
                  fontSize: 16,
                  color: "text.secondary",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  ml: 1,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </SideBarScroll>
  );
});

ReferenceDatasetsTab.displayName = "ReferenceDatasetsTab";

export default ReferenceDatasetsTab;
